import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, Loader2, Clock, Save, Users, Ticket as TicketIcon, RefreshCw, ArrowRight,
  CheckCircle2, Plug, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const LIVE_CHANNELS = [
  { key: 'viator', name: 'Viator', market: 'Global · Tripadvisor (fee-waived)', logo: '/ota-logos/viator.jpeg', color: '#0a7d5a', initials: 'V', provider: 'bokun' },
  { key: 'gyg', name: 'GetYourGuide', market: 'Western / European market', logo: '/ota-logos/getyourguide.png', color: '#ff5533', initials: 'G', provider: 'bokun' },
  { key: 'expedia', name: 'Expedia', market: 'Global market', logo: '/ota-logos/expedia.png', color: '#191e3b', initials: 'E', provider: 'bokun' },
  { key: 'klook', name: 'Klook', market: 'Southeast Asia / Malaysia', logo: '/ota-logos/klook.png', color: '#ff5b00', initials: 'K', provider: 'bokun' },
  { key: 'tripcom', name: 'Trip.com', market: 'East Asia / Global market', logo: '/ota-logos/tripcom.png', color: '#287dfa', initials: 'T', provider: 'bokun' },
  { key: 'civitatis', name: 'Civitatis', market: 'Spanish / LATAM market', logo: '/ota-logos/civitatis.png', color: '#ff6b00', initials: 'C', provider: 'bokun' },
  { key: 'tiqets', name: 'Tiqets', market: 'Attractions · Global', logo: '/ota-logos/tiqets.png', color: '#ff4d5a', initials: 'Ti', provider: 'bokun' },
  { key: 'musement', name: 'Musement', market: 'European market · TUI', logo: '/ota-logos/musement.png', color: '#0aa1c9', initials: 'M', provider: 'bokun' },
  { key: 'hotelbeds', name: 'Hotelbeds', market: 'B2B bedbank · Global', logo: '/ota-logos/hotelbeds.png', color: '#e4002b', initials: 'HB', provider: 'bokun' },
  { key: 'headout', name: 'Headout', market: 'India / APAC last-minute', logo: null, color: '#7c3aed', initials: 'H', provider: 'bokun' },
  { key: 'airbnb', name: 'Airbnb Experiences', market: 'Global market', logo: '/ota-logos/airbnb.jpeg', color: '#ff385c', initials: 'A', provider: 'bokun' },
  { key: 'google_things_to_do', name: 'Google Things to Do', market: 'Google Search & Maps · Global', logo: '/ota-logos/google-things-to-do.png', color: '#4285F4', initials: 'G', provider: 'bokun' },
];

const COMING_SOON = [
  { key: 'kkday', name: 'KKday', market: 'East / Southeast Asia', initials: 'KK', provider: 'globaltix' },
  { key: 'waug', name: 'WAUG', market: 'South Korea', initials: 'W', provider: 'globaltix' },
];

// Connectivity providers ("pipes") that power each OTA. Bókun is live; GlobalTix
// is pending API credentials. Badges below reflect which provider routes a channel.
const PROVIDERS = {
  bokun: { label: 'Bókun', color: '#0C2159', status: 'active' },
  globaltix: { label: 'GlobalTix', color: '#0aa15a', status: 'pending' },
};

const PoweredByBadge = ({ provider, testid }) => {
  const p = PROVIDERS[provider];
  if (!p) return null;
  const pending = p.status === 'pending';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${pending ? 'text-amber-600 border-amber-200 bg-amber-50' : 'text-slate-500 border-slate-200 bg-slate-50'}`}
      data-testid={testid}
      title={pending ? `${p.label} connection pending API credentials` : `Routed through ${p.label}`}
    >
      <Plug className="w-2.5 h-2.5" style={{ color: p.color }} /> Powered by {p.label}{pending ? ' · pending' : ''}
    </span>
  );
};

const ChannelLogo = ({ ch, size = 'w-12 h-12' }) => {
  const [failed, setFailed] = useState(false);
  return (ch.logo && !failed)
    ? <img src={ch.logo} alt={ch.name} onError={() => setFailed(true)}
        className={`${size} rounded-xl object-contain bg-white shrink-0 border border-slate-100 p-1`} />
    : <div className={`${size} rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0`} style={{ backgroundColor: ch.color }}>{ch.initials}</div>;
};

const ChannelStatusBadge = ({ status, testid }) => {
  const map = {
    LIVE: { cls: 'text-emerald-600', icon: CheckCircle2, label: 'Live' },
    REQUESTED: { cls: 'text-amber-600', icon: Clock, label: 'Requested' },
    DISCONNECTED: { cls: 'text-slate-400', icon: Clock, label: 'Setup pending' },
    PENDING: { cls: 'text-slate-400', icon: Clock, label: 'Setup pending' },
  };
  const s = map[status] || map.PENDING;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${s.cls}`} data-testid={testid}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
};

export default function EticketChannelManager({ onOpenProviderTab }) {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capacity, setCapacity] = useState(0);
  const [saving, setSaving] = useState(false);
  const [busyCh, setBusyCh] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [sgmc, setSgmc] = useState(null);
  const [sgmcChecked, setSgmcChecked] = useState(false);
  const [sgmcBusy, setSgmcBusy] = useState(false);
  const unitsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/eticket/octo/admin/connection`);
      setConn(data);
      setCapacity(data.daily_capacity || 0);
    } catch {
      setConn(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSgmc = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/eticket/octo/admin/sgmc`);
      setSgmc(data);
    } catch { setSgmc(null); }
  }, []);
  useEffect(() => { loadSgmc(); }, [loadSgmc]);

  const joinSgmc = async () => {
    if (!sgmcChecked) return;
    setSgmcBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/eticket/octo/admin/sgmc/join`, { accepted: true });
      setSgmc(data);
      toast.success('You have joined StayMii Global Market Connect.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not join SGMC. Please try again.');
    } finally { setSgmcBusy(false); }
  };

  const leaveSgmc = async () => {
    setSgmcBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/eticket/octo/admin/sgmc/leave`);
      setSgmc(data);
      setSgmcChecked(false);
      toast.success('You have left StayMii Global Market Connect.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed. Please try again.');
    } finally { setSgmcBusy(false); }
  };

  const saveCapacity = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/eticket/octo/admin/capacity`, { daily_capacity: Number(capacity) || 0 });
      await load();
    } finally { setSaving(false); }
  };

  const channelAction = async (key, action, successMsg) => {
    setBusyCh(key);
    try {
      const { data } = await axios.post(`${API}/api/eticket/octo/admin/channels/${key}/${action}`);
      setConn(prev => prev ? { ...prev, channels: data.channels } : prev);
      toast.success(successMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed. Please try again.');
    } finally { setBusyCh(null); }
  };

  const disconnectChannel = (key, name) => channelAction(key, 'disconnect', `${name} disconnected.`);
  const activateChannel = (key, name) => channelAction(key, 'activate', `${name} is now Live.`);

  // Provider-gated connect: an OTA can only be connected once its connectivity
  // provider ("pipe") is set up. Bókun-powered OTAs open a guided enrollment
  // checklist; GlobalTix-powered OTAs stay disabled until their API is live.
  const providers = conn?.providers || {};
  const handleConnect = (ch) => {
    const pr = providers[ch.provider] || {};
    if (!pr.ready) {
      if (ch.provider === 'globaltix') {
        toast.info('GlobalTix connection is pending API credentials. It will activate here once available.');
        return;
      }
      toast.info(`${ch.name} is powered by ${pr.label || 'Bókun'}. Set up ${pr.label || 'Bókun'} first.`);
      onOpenProviderTab && onOpenProviderTab();
      return;
    }
    // Provider is ready → record the request (REQUESTED) and open the checklist.
    channelAction(ch.key, 'connect', `${ch.name} enrollment started via ${pr.label}.`);
    setEnrollFor(ch);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#0C2159]" /></div>;
  }

  const connected = conn?.connected;
  const isAdmin = conn?.is_platform_admin;
  const channels = conn?.channels || {};

  return (
    <div className="space-y-6" data-testid="octo-channel-manager">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Globe className="w-6 h-6 text-cyan-500" /> StayMii Global Market Connect
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            Instantly sync your tickets and live capacity with the world's leading travel marketplaces.
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 dark:text-slate-300 dark:border-slate-700 shrink-0"
          data-testid="octo-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {!connected && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700" data-testid="octo-disconnected">
          We couldn't load an E-Ticket workspace for your account. Make sure your attraction workspace is set up,
          then refresh to manage your marketplace connections.
        </div>
      )}

      {connected && (
        <>
          {/* SGMC optional opt-in */}
          <div className="rounded-2xl border border-cyan-200 dark:border-cyan-900/50 bg-cyan-50/60 dark:bg-cyan-950/20 p-5" data-testid="sgmc-optin">
            {sgmc?.joined ? (
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">You've joined StayMii Global Market Connect</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Accepted v{sgmc.version} · {sgmc.accepted_at ? new Date(sgmc.accepted_at).toLocaleDateString() : ''} ·{' '}
                      <a href="/legal/sgmc-terms" target="_blank" rel="noreferrer" className="text-cyan-700 underline">View SGMC Terms</a>
                    </p>
                  </div>
                </div>
                <button onClick={leaveSgmc} disabled={sgmcBusy}
                  className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-60"
                  data-testid="sgmc-leave-btn">
                  {sgmcBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Leave SGMC'}
                </button>
              </div>
            ) : (
              <div>
                <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-500" /> Enable StayMii Global Market Connect
                </p>
                <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                  Participation in SGMC is optional. Join to distribute your tickets across our network of SGMC channels and marketplaces.
                </p>
                <label className="flex items-start gap-3 mt-3 cursor-pointer">
                  <input type="checkbox" checked={sgmcChecked} onChange={e => setSgmcChecked(e.target.checked)}
                    className="mt-0.5 h-5 w-5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer shrink-0"
                    data-testid="sgmc-accept-checkbox" />
                  <span className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    Join StayMii Global Market Connect. By joining SGMC, I agree to the{' '}
                    <a href="/legal/sgmc-terms" target="_blank" rel="noreferrer" className="text-cyan-700 font-semibold underline">StayMii Global Market Connect (SGMC) Terms &amp; Conditions</a>.
                  </span>
                </label>
                <button onClick={joinSgmc} disabled={!sgmcChecked || sgmcBusy}
                  className="mt-3 flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="sgmc-join-btn">
                  {sgmcBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Join StayMii Global Market Connect
                </button>
              </div>
            )}
          </div>

          {/* Live connections grid */}
          <div data-testid="octo-live-channels">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-cyan-500" />
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide">Live Connections</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {LIVE_CHANNELS.map(ch => {
                const status = channels[ch.key]?.status || 'PENDING';
                const busy = busyCh === ch.key;
                return (
                <div key={ch.key} className="bg-white dark:bg-[#131a2e] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 flex items-center gap-4"
                  data-testid={`octo-channel-${ch.key}`}>
                  <ChannelLogo ch={ch} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900 dark:text-white truncate">{ch.name}</p>
                      <ChannelStatusBadge status={status} testid={`octo-channel-status-${ch.key}`} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <p className="text-xs text-slate-500 truncate">{ch.market}</p>
                      <PoweredByBadge provider={ch.provider} testid={`octo-powered-${ch.key}`} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {status === 'PENDING' || status === 'DISCONNECTED' ? (
                      (() => {
                        const pr = providers[ch.provider] || {};
                        if (!pr.ready) {
                          return ch.provider === 'globaltix' ? (
                            <button disabled
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-slate-100 text-slate-400 cursor-not-allowed"
                              data-testid={`octo-connect-${ch.key}`}>
                              <Clock className="w-3.5 h-3.5" /> Pending API
                            </button>
                          ) : (
                            <button onClick={() => handleConnect(ch)} disabled={busy}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#0C2159] text-[#0C2159] hover:bg-[#0C2159]/5 disabled:opacity-60"
                              data-testid={`octo-connect-${ch.key}`}>
                              <Plug className="w-3.5 h-3.5" /> Connect {pr.label || 'Bókun'} first
                            </button>
                          );
                        }
                        return (
                          <button onClick={() => handleConnect(ch)} disabled={busy}
                            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-[#0C2159] text-white hover:bg-[#0a1a47] disabled:opacity-60"
                            data-testid={`octo-connect-${ch.key}`}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Connect
                          </button>
                        );
                      })()
                    ) : (
                      <>
                        {status === 'REQUESTED' && (
                          <button onClick={() => setEnrollFor(ch)}
                            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600"
                            data-testid={`octo-finish-${ch.key}`}>
                            <ArrowRight className="w-3.5 h-3.5" /> Finish in Bókun
                          </button>
                        )}
                        {isAdmin && status === 'REQUESTED' && (
                          <button onClick={() => activateChannel(ch.key, ch.name)} disabled={busy}
                            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                            data-testid={`octo-activate-${ch.key}`}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Mark Live
                          </button>
                        )}
                        <button onClick={() => disconnectChannel(ch.key, ch.name)} disabled={busy}
                          className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5"
                          data-testid={`octo-disconnect-${ch.key}`}>
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Disconnect'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Coming soon grid */}
          <div data-testid="octo-coming-soon">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Coming Soon</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {COMING_SOON.map(ch => (
                <div key={ch.key} className="bg-slate-50 dark:bg-[#0f1526] border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-5 flex items-center gap-3 opacity-70"
                  data-testid={`octo-channel-${ch.key}`}>
                  <div className="w-10 h-10 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-300 font-bold text-sm shrink-0">
                    {ch.initials}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-600 dark:text-slate-300 truncate">{ch.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{ch.market}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                        <Clock className="w-3 h-3" /> Coming soon
                      </span>
                      <PoweredByBadge provider={ch.provider} testid={`octo-powered-${ch.key}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bókun Marketplace note */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 dark:bg-[#0f1526] dark:border-slate-800 p-5" data-testid="bokun-marketplace-note">
            <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#0C2159]" /> Bókun Marketplace — 27,000+ resellers &amp; agents
            </p>
            <p className="text-xs text-slate-500 mt-1 max-w-3xl">
              Beyond the SGMC channels above, connecting Bókun also exposes your tickets to Bókun's Marketplace of
              27,000+ travel agents, DMCs, hotels, concierges and resellers worldwide. Contracts are managed
              from the Bókun dashboard once your plugin is live — set it up under the <b>SGMC Connect</b> tab.
            </p>
          </div>

          {/* Daily capacity — unchanged */}
          <div className="bg-white dark:bg-[#131a2e] border border-slate-200 dark:border-slate-800 rounded-2xl p-6" data-testid="octo-capacity-card">
            <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Users className="w-4 h-4 text-cyan-500" /> Daily Capacity</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">Total tickets available per day across all channels. Set <b>0</b> for unlimited.</p>
            <div className="flex items-center gap-3">
              <input type="number" min="0" value={capacity} onChange={e => setCapacity(e.target.value)}
                className="w-40 px-3 py-2 border border-slate-300 dark:border-slate-700 dark:bg-[#0B1020] dark:text-white rounded-lg text-sm"
                data-testid="octo-capacity-input" />
              <button onClick={saveCapacity} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-[#0C2159] text-white rounded-lg text-sm font-medium hover:bg-[#0a1a47] disabled:opacity-60"
                data-testid="octo-capacity-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
              <span className="text-xs text-slate-400">{Number(capacity) === 0 ? 'Unlimited' : `${capacity} / day`}</span>
            </div>
          </div>

          {/* Mapped ticket types — unchanged */}
          <div ref={unitsRef} className="bg-white dark:bg-[#131a2e] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 scroll-mt-24" data-testid="octo-units-card">
            <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-3"><TicketIcon className="w-4 h-4 text-cyan-500" /> Mapped Ticket Types (Channel Units)</p>
            <div className="space-y-2">
              {(conn.units || []).map(u => (
                <div key={u.id} className="flex items-center justify-between text-sm border-b border-slate-100 dark:border-slate-800 pb-2 last:border-0" data-testid={`octo-unit-${u.id}`}>
                  <span className="text-slate-700 dark:text-slate-200">{u.name}</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{conn.currency} {Number(u.price).toFixed(2)}</span>
                </div>
              ))}
              {(!conn.units || conn.units.length === 0) && <p className="text-sm text-slate-400">No active ticket types. Add rates first.</p>}
            </div>
            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> These ticket types &amp; prices are shared across all connected marketplaces.
            </p>
          </div>
        </>
      )}

      {/* Guided enrollment checklist (Bókun-powered OTAs) */}
      {enrollFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="ota-enroll-modal" onClick={() => setEnrollFor(null)}>
          <div className="bg-white dark:bg-[#131a2e] rounded-2xl max-w-lg w-full p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-1">
              <ChannelLogo ch={enrollFor} size="w-10 h-10" />
              <div>
                <p className="font-bold text-slate-900 dark:text-white">Enable {enrollFor.name} via {(providers[enrollFor.provider] || {}).label || 'Bókun'}</p>
                <p className="text-xs text-slate-500">Complete these steps in your Bókun dashboard.</p>
              </div>
            </div>
            <ol className="space-y-2 text-sm text-slate-600 dark:text-slate-300 mt-4 list-decimal pl-5">
              <li>Open your Bókun dashboard → <b>Sales tools → Sales channels</b>.</li>
              <li>Select <b>{enrollFor.name}</b> and accept its contract.</li>
              <li>Map your product <span className="font-mono bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{conn?.workspace_slug}</span> to {enrollFor.name}.</li>
            </ol>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-700 mt-4 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              Once enabled, Bókun pulls your availability &amp; prices and sends bookings back to StayMii automatically — each issues a QR e-ticket.
            </div>
            <div className="flex items-center justify-between gap-3 mt-5">
              <a href={(providers[enrollFor.provider] || {}).dashboard_url || 'https://bokun.io'} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-[#0C2159] text-white hover:bg-[#0a1a47]" data-testid="ota-enroll-open-bokun">
                <ArrowRight className="w-4 h-4" /> Open Bókun dashboard
              </a>
              <button onClick={() => setEnrollFor(null)} className="text-sm font-medium px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="ota-enroll-close">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
